import { BigNumber } from '@ethersproject/bignumber';
import { Tooltip, TooltipContent, TooltipOrigin } from '@silo/ui';
import { BigNumberWithDecimals, createBigNumberWithDecimals } from '@silo/types';
import { InfoIcon } from '@silo/ui';
import { FULL_AMOUNT_THRESHOLD } from 'app/config/networks';
import { convertCurrency } from 'app/functions/silo/calculations';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { FC } from 'react';

type DecideIsFullAmount = (params: {
  balance: BigNumber;
  selectedAsset: SiloAsset;
  amountInUSD: BigNumberWithDecimals | null;
  priceInUSD: BigNumberWithDecimals | null;
  normalizedTokenAmount: BigNumber | null;
}) => boolean;

/**
 * Decides if a generic withdrawal amount constitutes a full withdrawal. Used by both the normal withdraw flow and the dual collateral flow.
 */
export const decideIsFullAmount: DecideIsFullAmount = ({
  selectedAsset,
  amountInUSD,
  priceInUSD,
  balance,
  normalizedTokenAmount,
}) => {
  if (!amountInUSD || !priceInUSD) {
    /**
     * USD-based detection is unavailable (e.g. the oracle price is missing). Fall
     * back to a token comparison so the user can still fully close the position.
     * Use a 1% tolerance rather than an exact match: UI display amounts are
     * rounded, so requiring an exact match would make a 100% withdrawal
     * impractical.
     */
    if (!normalizedTokenAmount || balance.isZero()) return false;

    const tolerance = balance.div(100); // 1% of balance

    return balance.sub(normalizedTokenAmount).abs().lte(tolerance);
  }

  const collateralBalance = createBigNumberWithDecimals(balance, selectedAsset.decimals);

  const collateralBalanceInUSD = convertCurrency(collateralBalance, priceInUSD);

  if (amountInUSD.bigNumber.gt(collateralBalanceInUSD.bigNumber)) {
    return false;
  }

  const difference = collateralBalanceInUSD.bigNumber.sub(amountInUSD.bigNumber);

  return difference.lt(FULL_AMOUNT_THRESHOLD.bigNumber);
};

export const FullAmountWarning: FC = () => {
  return (
    <div className="flex items-center gap-2 text-white">
      <p>Your position will be fully withdrawn.</p>
      <Tooltip placement="right">
        <TooltipOrigin>
          <InfoIcon className="w-5 h-5" />
        </TooltipOrigin>
        <TooltipContent>
          If you enter an amount to withdraw that is within $1 of your total deposited, your position will be
          automatically closed.
        </TooltipContent>
      </Tooltip>
    </div>
  );
};
